// src/context/MessagesContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { apiCore } from "../api/apiCore";
import * as url from "../api/urls";
import { useSocket } from "./SocketContext";
import { IMessage, IAttachment, ISession, isWithin24Hours, mapAttachmentToTypes } from "../types/message"; // IMessage é a interface do backend (sem image)
import { IMessagesTypes } from "../types/message"; // apenas para tipagem do 
import { useAuth } from "../context/AuthContext";
import { IContact } from "../types/contacts";
import { getBlockedContact, getBookmarkContact, getMarkedContact } from "../api";
import { useContacts } from "../hooks/useContacts";

export interface MessagesContextType {
  lastSignalRMessageRef: React.MutableRefObject<IMessage | null>;
  lastMessage: IMessage | null;
  updatedMessage: IMessage | null;
  messages: IMessage[];

  sessions: ISession[];
  contactNoAssigned: IContact[];
  favourites: IContact[];
  contactMarked: IContact[];
  contactBlocked: IContact[];
  loading: boolean;
  error: string | null;

  // Atualização de status (sent, delivered, read)
  handleStatusUpdate: (idMessage: number, status: string) => void;
  lastStatusUpdate: { idMessage: number; status: string } | null;

  // Loads
  loadSessionContactOpen: (idContact: number) => Promise<boolean>;
  loadContactsMarked: () => Promise<IContact[]>;
  loadContactsBlocked: () => Promise<IContact[]>;
  loadFavourites: () => Promise<IContact[]>;
  loadContactAssigned: () => Promise<IContact[]>;
  loadSessionsActive: () => Promise<ISession[]>;
  loadMessagesTypes: (userId: number, contactId: number) => Promise<IMessagesTypes[]>;
  loadMessages: (idContact: number) => Promise<void>;

  loadAllUserConversations: (userId: number) => Promise<IMessage[]>;
  loadMessagesNoUserAssigned: () => Promise<IMessage[]>;

  // CRUD
  sendTransferSessionMessage: (data: object) => Promise<boolean>;
  sendAudioTemplateMessage: (data: object) => Promise<IMessage>;
  sendAudioMessage: (data: FormData) => Promise<IMessage>;
  sendTemplateMessage: (data: object) => Promise<IMessage>;
  sendReactionMessage: (data: object) => Promise<IMessage>;
  sendCloseSessionMessage: (data: object) => Promise<IMessage>;
  sendMessage: (data: object) => Promise<IMessage>;
  updateMessage: (id: number, data: Partial<IMessage>) => Promise<void>;
  deleteMessage: (id: number) => Promise<void>;

  // NOVAS FUNÇÕES ADICIONADAS (para attachments que vêm separados)
  upsertAttachments: (attachments: IAttachment[]) => void;
  getAttachmentsForMessage: (idMessage: number) => IAttachment[]; // retorna [] se não houver
  markMessagesAsRead: (contactId: number) => void;
}

export const MessagesContext = createContext<MessagesContextType | null>(null);

export const MessagesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

  const { user } = useAuth();

  const [lastStatusUpdate, setLastStatusUpdate] = useState<{ idMessage: number; status: string } | null>(null);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sessions, setSessions] = useState<ISession[]>([]);
  const [contactNoAssigned, setContactNoAssigned] = useState<IContact[]>([]);
  const [favourites, setFavourites] = useState<IContact[]>([]);
  const [contactMarked, setContactMarked] = useState<IContact[]>([]);
  const [contactBlocked, setContactBlocked] = useState<IContact[]>([]);

  // guarda a última mensagem que veio via SignalR (objeto IMessage)
  const lastSignalRMessageRef = useRef<IMessage | null>(null);

  // cache primário de mensagens (Map id -> IMessage)
  const messageCache = useRef(new Map<number, IMessage>()).current;

  // 🟦 AQUI: item 1 (state que guarda attachments)
  const [attachmentsMap, setAttachmentsMap] = useState<Map<number, IAttachment[]>>(new Map());
  const lastMessage = useMemo(() => messages[messages.length - 1] ?? null, [messages]);
  const [updatedMessage, setUpdatedMessage] = useState<IMessage | null>(null);

  const { contacts } = useContacts();
  const contactsRef = useRef<IContact[]>([]);

  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);

  // -------------------------------------------------------------
  // MARCAR TODAS MENSAGENS DE UM CONTATO COMO LIDAS
  // -------------------------------------------------------------
  const markMessagesAsRead = useCallback((contactId: number) => {
    setMessages(prev =>
      prev.map(m =>
        m.idContact === contactId ? { ...m, isRead: 1 } : m
      )
    );
  }, []);

  // -------------------------------------------------------------
  // MERGE SEGURO (imutável) — apenas campos de IMessage
  // ------------------------------------------------------------- 
  const mergeMessages = useCallback((newMessages: IMessage[]) => {

    // console.log("🟦 mergeMessages() → Recebido newMessages:", JSON.parse(JSON.stringify(newMessages)));

    setMessages(prev => {

      // console.log("🟨 mergeMessages() → Estado ANTERIOR messages:", JSON.parse(JSON.stringify(prev)));

      const map = new Map<number, IMessage>();

      // adiciona existentes
      prev.forEach(m => map.set(m.id, m));

      // insere / atualiza sem mutação
      newMessages.forEach(m => {

        // const existedBefore = map.has(m.id);

        // console.log(
        //   `🔍 mergeMessages() → Mensagem id=${m.id} existedBefore=${existedBefore}`,
        //   "\nMensagem nova:", JSON.parse(JSON.stringify(m)),
        //   existedBefore ? "\nMensagem anterior no map:" : "",
        //   existedBefore ? JSON.parse(JSON.stringify(map.get(m.id))) : ""
        // );

        const existing = map.get(m.id);

        if (!existing) {
          // console.log(`🟢 Inserindo nova mensagem id=${m.id}`);
          map.set(m.id, { ...m });
        } else {
          // console.log(`🟠 Atualizando mensagem existente id=${m.id}`);
          map.set(m.id, { ...existing, ...m });
        }
      });

      // ordena por id
      const arr = Array.from(map.values()).sort((a, b) => a.id - b.id);

      // console.log("🟩 mergeMessages() → Após merge, novo estado:", JSON.parse(JSON.stringify(arr)));

      return arr;
    });
  }, []);

  // -------------------------------------------------------------
  // LOAD NORMAL (carrega IMessage para useChats / unread / notificações)
  // -------------------------------------------------------------
  const loadMessages = useCallback(async (idContact: number) => {
    try {
      setLoading(true);
      const list = await apiCore<IMessage[]>(`${url.MESSAGES}/${idContact}/contact`); // <-- ajuste o endpoint correto
      // console.log('loadMessages', list)
      if (Array.isArray(list)) {
        mergeMessages(list);
      }
    } catch (e) {
      console.error("[MessagesContext] erro ao carregar mensagens", e);
      setError("Erro ao carregar mensagens");
    } finally {
      setLoading(false);
    }
  }, [mergeMessages]);

  // -------------------------------------------------------------
  // LOAD V2 -> retorna IMessagesTypes[] (já formatadas para a UI)
  // -------------------------------------------------------------
  const loadMessagesTypes = useCallback(async (userId: number, contactId: number): Promise<IMessagesTypes[]> => {
    try {

      setLoading(true);
      const respone = await apiCore<{
        conversationId: number;
        userId: string;
        typingUser: string;
        messages: IMessagesTypes[];
      }>(`${url.MESSAGES}/${userId}/${contactId}/chat`);

      // Se o backend retornar attachments separados dentro de IMessagesTypes.messages, ok.
      return respone?.data.messages ?? [];

    } catch (err) {
      console.error("[MessagesContext] loadMessagesTypes error", err);
      setError("Erro ao carregar mensagens (types)");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // -------------------------------------------------------------
  // SEND
  // -------------------------------------------------------------
  const sendMessage = useCallback(async (data: object) => {
    //console.log("[MessagesContext] sendMessage", data);
    const response = await apiCore<IMessage>(url.SEND_MESSAGE_DIRECT, { method: "POST", body: data });
    messageCache.set(response.data.id, response.data);
    mergeMessages([response.data]);

    return response.data;
  }, [mergeMessages]);

  // -------------------------------------------------------------
  // UPDATE
  // -------------------------------------------------------------
  const updateMessage = useCallback(async (id: number, data: Partial<IMessage>) => {
    const response = await apiCore<IMessage>(`${url.MESSAGES}/${id}`, { method: "PUT", body: data });
    messageCache.set(response.data.id, response.data);
    mergeMessages([response.data]);
  }, [mergeMessages]);

  // -------------------------------------------------------------
  // DELETE
  // -------------------------------------------------------------
  const deleteMessage = useCallback(async (id: number) => {
    await apiCore(`${url.MESSAGES}/${id}`, { method: "DELETE" });
    messageCache.delete(id);
    setMessages(prev => prev.filter(m => m.id !== id));
  }, [messageCache]);

  // -------------------------------------------------------------
  // UPDATING ATTACHMENTS MAP (quando attachments chegam separados)
  // -------------------------------------------------------------
  const upsertAttachments = useCallback(
    (items: IAttachment[]) => {

      // 1️⃣ Atualiza o map (continua igual)
      setAttachmentsMap(prev => {
        const map = new Map(prev);

        items.forEach(att => {
          const idMsg = att.idMessage;
          if (!idMsg) return;

          const existing = map.get(idMsg) ?? [];

          const merged = [
            ...existing.filter(e => e.id !== att.id),
            att,
          ];

          map.set(idMsg, merged);
        });

        return map;
      });
    },
    []
  );

  const getAttachmentsForMessage = useCallback((idMessage: number): IAttachment[] => {
    const attached = attachmentsMap.get(idMessage) ?? [];
    // console.log('[MessagesContext] getAttachmentsForMessage', idMessage, attached);
    return attached;
  }, [attachmentsMap]);

  const messagesWithAttachments = useMemo(() => {
    return messages.map(msg => {
      const atts = attachmentsMap.get(msg.id) ?? [];
      // console.log('[MessagesContext] messagesWithAttachments', msg.id, atts);
      return {
        ...msg,
        attachments: atts.map(mapAttachmentToTypes),
      };
    });
  }, [messages, attachmentsMap]);


  // -------------------------------------------------------------
  // Send Audio ( Message )
  // -------------------------------------------------------------
  const sendAudioMessage = useCallback(
    async (data: FormData) => {
      const response = await apiCore<IMessage>(
        url.SEND_MESSAGE_MEDIA,
        {
          method: "POST",
          body: data,
          // ⚠️ NÃO colocar headers aqui
        }
      );

      messageCache.set(response.data.id, response.data);
      mergeMessages([response.data]);

      return response.data;
    },
    [mergeMessages]
  );

  // -------------------------------------------------------------
  // Send Audio ( Message )
  // -------------------------------------------------------------
  const sendAudioTemplateMessage = useCallback(
    async (data: object) => {
      const response = await apiCore<IMessage>(
        url.SEND_MESSAGE_MEDIA_TEMPLATE,
        {
          method: "POST",
          body: data,
          // ⚠️ NÃO colocar headers aqui
        }
      );

      messageCache.set(response.data.id, response.data);
      mergeMessages([response.data]);

      return response.data;
    },
    [mergeMessages]
  );

  // -------------------------------------------------------------
  // Send Reaction ( Message )
  // -------------------------------------------------------------
  const sendReactionMessage = useCallback(async (data: object) => {
    // console.log("[MessagesContext] sendReactionMessage", data);
    const response = await apiCore<IMessage>(url.SEND_MESSAGE_REACTION, { method: "POST", body: data });
    messageCache.set(response.data.id, response.data);
    mergeMessages([response.data]);

    return response.data;
  }, [mergeMessages]);

  // -------------------------------------------------------------
  // Send Message Template
  // -------------------------------------------------------------
  const sendTemplateMessage = useCallback(async (data: object) => {
    // console.log("[MessagesContext] sendReactionMessage", data);
    const response = await apiCore<IMessage>(url.SEND_MESSAGE_TEMPLATE, { method: "POST", body: data });
    messageCache.set(response.data.id, response.data);
    mergeMessages([response.data]);

    return response.data;
  }, [mergeMessages]);

  // -------------------------------------------------------------
  // Close Session ( Message )
  // -------------------------------------------------------------
  const sendCloseSessionMessage = useCallback(async (data: object) => {
    // console.log('[sendCloseSessionMessage]', data)

    const response = await apiCore<IMessage>(url.SEND_MESSAGE_CLOSE, { method: "POST", body: data });
    // console.log('[sendCloseSessionMessage][response]', response.data)
    messageCache.set(response.data.id, response.data);
    mergeMessages([response.data]);

    return response.data;
  }, [mergeMessages]);

  // -------------------------------------------------------------
  // Transfer Session user ( Message )
  // -------------------------------------------------------------
  const sendTransferSessionMessage = useCallback(async (data: object) => {
    console.log("[sendTransferSessionMessage] data", data);
    const response = await apiCore<boolean>(url.SEND_MESSAGE_TRANSFER_SESSION, { method: "POST", body: data });
    console.log("[sendTransferSessionMessage] response", response.data);

    return response.data;
  }, [mergeMessages]);


  //LOAD: Session Active (para montar menu de conversas finalizadas (com session fechadas))
  const loadSessionsActive = useCallback(
    async (): Promise<ISession[]> => {

      if (!user?.id) {
        // console.warn("[loadSessionsActive] Usuário ou idEnterprise não disponível", user);
        return [];
      }
      // console.log("[loadSessionsActive] leitura sessions", user.id);
      try {
        const response = await apiCore<ISession[]>(
          `${url.SESSION}/${user.id}`
        );

        setSessions(response.data ?? []);
        // console.log("[loadSessionsActive] sessions", response.data);

        return response.data ?? [];
      } catch (e) {
        console.error("[MessagesContext] loadSessionsActive", e);
        return [];
      }
    },
    [user] // 👈 ESSENCIAL
  );

  const loadSessionContactOpen = useCallback(
    async (idContact: number): Promise<boolean> => {

      // console.log("[loadSessionContactOpen] idContact", idContact);

      if (!user?.id) {
        // console.warn("[loadSessionsActive] Usuário ou idEnterprise não disponível", user);
        return false;
      }

      // console.log("[loadSessionContactOpen] leitura sessions", user.id);
      try {
        const response = await apiCore<ISession>(
          `${url.SESSION_OPEN}/${idContact}/Sessions`
        );

        if (!response.data) {
          // console.log("[loadSessionContactOpen] Session 204");
          return true
        }


        // console.log("[loadSessionContactOpen] Session", response);

        if (response.data.idUser === 3) {
          return true
        }

        const userSession =
          response.data.idUser === user.id;

        // const userSession = response.data.idUser == user.id
        // console.log("[loadSessionContactOpen] userSession", userSession);

        return userSession;
      } catch (e) {
        console.error("[MessagesContext] loadSessionContactOpen", e);
        return false;
      }
    },
    [user] // 👈 ESSENCIAL
  );

  const loadContactAssigned = useCallback(
    async (): Promise<IContact[]> => {

      if (!user?.id) {
        // console.warn("[loadcontactAssigned] Usuário ou idEnterprise não disponível", user);
        return [];
      }

      try {
        // console.log('[messagesContext] loadContactAssigned - enterpriseId:', user.idEnterprise)
        const response = await apiCore<IContact[]>(
          `${url.MESSAGES}/${user.idEnterprise}/ContactsNoAssigned`
        );

        setContactNoAssigned(response.data ?? []);

        return response.data ?? [];
      } catch (e) {
        console.error("[MessagesContext] loadcontactAssigned", e);
        return [];
      }
    },
    [user] // 👈 ESSENCIAL
  );

  const loadContactsMarked = useCallback(async () => {
    if (!user?.id) return [];

    try {
      const response = await getMarkedContact(user.id);
      setContactMarked(response ?? []);
      return response ?? [];
    } catch (e) {
      console.error("[MessagesContext] loadContactsMarked", e);
      return [];
    }
  }, [user]);

  const loadContactsBlocked = useCallback(async () => {
    if (!user?.id) return [];

    // console.log('[loadContactsBlocked][userId]', user.id)

    try {
      const response = await getBlockedContact(user.id);
      setContactBlocked(response ?? []);
      return response ?? [];
    } catch (e) {
      console.error("[MessagesContext] loadContactsBlocked", e);
      return [];
    }
  }, [user]);

  const loadFavourites = useCallback(async () => {
    if (!user?.id) return [];

    try {
      const response = await getBookmarkContact(user.id);
      setFavourites(response ?? []);
      return response ?? [];
    } catch (e) {
      console.error("[MessagesContext] loadFavourites", e);
      return [];
    }
  }, [user]);

  const handleAssignedUpdated = useCallback(
    (notAssigneds: IContact[]) => {
      // console.log("[MessagesContext] handleAssignedUpdated", notAssigneds);
      // 1️⃣ Atualiza SOMENTE a lista de sem atribuição
      setContactNoAssigned(notAssigneds);
    },
    [setContactNoAssigned]
  );

  const handleSessionUpdated = useCallback((session: ISession) => {
    console.log('handleSessionUpdated', session)
    setSessions(prev => {
      const exists = prev.some(s => s.id === session.id);

      if (!exists) {
        return [...prev, session];
      }

      return prev.map(s =>
        s.id === session.id ? { ...s, ...session } : s
      );
    });

  }, []);

  // -------------------------------------------------------------
  // LOAD: Todas conversas do usuário logado (para unread)
  // -------------------------------------------------------------
  const loadAllUserConversations = useCallback(async (userId: number): Promise<IMessage[]> => {
    try {
      console.log('[loadAllUserConversations][userId]', userId)
      return [];


      const response = await apiCore<IMessage[]>(
        `${url.MESSAGES}/${userId}/user`
      );
      console.log('[loadAllUserConversations][count]', response.data.length);

      if (Array.isArray(response.data)) {
        mergeMessages(response.data);
      }

      return response.data ?? [];
    } catch (e) {
      console.error("[MessagesContext] loadAllUserConversations", e);
      return [];
    }
  }, [mergeMessages]);

  // -------------------------------------------------------------
  // LOAD: Mensagens sem usuário atribuído
  // -------------------------------------------------------------
  const loadMessagesNoUserAssigned = useCallback(async (): Promise<IMessage[]> => {
    try {

      if (!user?.idEnterprise) return [];

      console.log('[loadMessagesNoUserAssigned][idEnterprise]', user?.idEnterprise)
      return [];

      const response = await apiCore<IMessage[]>(
        `${url.MESSAGES}/${user?.idEnterprise}/MessagesNoUserAssigned`
      );

      console.log('[loadMessagesNoUserAssigned][count]', response.data.length);

      if (Array.isArray(response.data)) {
        mergeMessages(response.data);
      }

      return response.data ?? [];
    } catch (e) {
      console.error("[MessagesContext] loadMessagesNoUserAssigned", e);
      return [];
    }
  }, [user?.idEnterprise, mergeMessages]);

  // -------------------------------------------------------------
  // Quando chegar nova mensagem via SignalR
  // -------------------------------------------------------------
  const handleIncomingMessage = useCallback((msg: IMessage) => {
    if (!msg) return;

    // console.log("📩 Mensagem recebida:", msg);

    // Ignorar tipos inválidos
    if (["interactive", "unsupported", "error", "fail"].includes(msg.type)) return;

    // console.log("📩 Mensagem IdEnterprise:", msg.idEnterprise);
    // console.log("📩 Mensagem IdUserMain:", msg.idUserMain);
    // console.log("📩 Mensagem IdUser:", msg.idUser);

    // console.log("📩 Chat IdUser:", user?.id);
    // console.log("📩 Chat IdEnterprise:", user?.idEnterprise);

    if (!user) {
      console.warn("[handleIncomingMessage] user ainda não carregado");
      return;
    }

    const contact = contactsRef.current.find(c => c.id === msg.idContact);

    if (!contact) {
      console.log('[ReceiveMessage] contato nao encontrado');
      return;
    }

    // 🔧 ID do usuário IA (pegar da config / enterprise)
    const idUserIA = 1;

    // 🔐 mesma empresa
    const isSameEnterprise = msg.idEnterprise === user.idEnterprise;

    // 👤 mensagem do próprio atendente
    const isMessageFromUser = msg.idUser === user.id;

    // 🟡 sem atendente atribuído (fila geral)
    const isWithoutAssignedUser = msg.idUser === msg.idUserMain;

    // 👑 usuário principal da empresa
    const isMainUser = user.id === msg.idUserMain;

    // 👑 usuário principal da empresa
    const isRobotUser = msg.idUser === idUserIA;

    // ✅ regra final
    const canReceive =
      isSameEnterprise &&
      (isMessageFromUser || isWithoutAssignedUser || isMainUser || isRobotUser);

    if (!canReceive) {
      console.warn("⛔ Mensagem ignorada (regra multi-atendente)", {
        msgUser: msg.idUser,
        userId: user.id,
        idUserMain: msg.idUserMain,
      });
      return;
    }

    lastSignalRMessageRef.current = msg;

    const cloned = { ...msg };
    messageCache.set(cloned.id, cloned);
    mergeMessages([cloned]);

    setUpdatedMessage(cloned);

  }, [mergeMessages, messageCache, user]);

  // -------------------------------------------------------------
  // Atualização de status (sent, delivered, read) — altera apenas status de IMessage
  // -------------------------------------------------------------
  const handleStatusUpdate = useCallback((idMessage: number, status: string) => {

    setMessages(prev =>
      prev.map(msg => (msg.id === idMessage ? { ...msg, status } : msg))
    );
    setLastStatusUpdate({ idMessage, status });
  }, []);

  // -------------------------------------------------------------
  // SIGNALR subscriptions
  // -------------------------------------------------------------
  const { connection, subscribeEvent } = useSocket();
  useEffect(() => {

    // console.log("🟢 Subscribing SignalR events...");

    if (!connection || connection.state !== "Connected" || !user) {
      console.log("[SignalR] aguardando conexão válida para subscribe...");
      return;
    }

    const unsubNew = subscribeEvent<{ message: IMessage }>("ReceiveMessage", (data) => {
      try {
        // console.log("[MessagesContext] ReceiveMessage", data);

        // 🚨 evento ainda chega antes do auth montar
        if (!user?.id) return;

        // 🧠 multi-atendente isolation
        const { idUser, idUserMain } = data.message;

        // 🔧 ID do usuário IA (pegar da config / enterprise)
        const idUserIA = 1;

        // 🚫 BLOQUEIA SOMENTE SE:
        // - não é minha
        // - não é contato novo da empresa
        // if (idUser !== user.id && idUser !== idUserMain) {
        //   console.log('[SignalR][ReceiveMessage] Ignorado - sessão de outro atendente');
        //   return;
        // }

        const isMine = idUser === user.id;
        const isQueue = idUser === idUserMain;
        const isRobot = idUser === idUserIA;

        // 🚫 BLOQUEIA apenas se NÃO for nenhum dos casos válidos
        if (!isMine && !isQueue && !isRobot) {
          console.log('[SignalR][ReceiveMessage] Ignorado - outro atendente', {
            idUser,
            idUserMain,
            userId: user.id
          });
          return;
        }

        handleIncomingMessage(data.message);
      }
      catch (e) {
        console.error(e);
      }
    });

    const unsubUpdate = subscribeEvent<{ message: IMessage }>("ReceiveEvent", (data) => {
      try {
        // console.log("[MessagesContext] ReceiveEvent", data);

        // 🚨 evento ainda chega antes do auth montar
        if (!user?.id) return;

        // 🧠 multi-atendente isolation
        const { idUser, idUserMain } = data.message;

        // 🚫 BLOQUEIA SOMENTE SE:
        // - não é minha
        // - não é contato novo da empresa
        if (
          idUser !== user.id &&
          idUser !== idUserMain
        ) {
          console.log('[SignalR][ReceiveEvent]  Ignorado - sessão de outro atendente');
          return;
        }

        handleIncomingMessage(data.message);
      } catch (e) { console.error(e); }
    });

    // Caso backend envie attachments via SignalR num evento específico:
    const unsubAttachments = subscribeEvent<{ attachment: IAttachment }>("ReceiveAttachments", (data) => {
      try {

        // console.log("[MessagesContext] ReceiveAttachments", data);

        // 🚨 evento ainda chega antes do auth montar
        if (!user?.id) return;

        // 🧠 multi-atendente isolation
        const { idUser, idUserMain } = data.attachment;

        // 🚫 BLOQUEIA SOMENTE SE:
        // - não é minha
        // - não é contato novo da empresa
        if (
          idUser !== user.id &&
          idUser !== idUserMain
        ) {
          console.log('[SignalR][ReceiveAttachments] Ignorado - sessão de outro atendente');
          return;
        }

        upsertAttachments([data.attachment]);

      } catch (e) { console.error(e); }
    });

    const unsubStatus = subscribeEvent<{ idMessage: number; idUser: number; idUserMain: number; idEnterprise: number; status: string }>("ReceiveMessageStatus", (data) => {
      try {
        // console.log("[MessagesContext] ReceiveMessageStatus", data);

        // 🚨 evento ainda chega antes do auth montar
        if (!user?.id) return;

        // 🧠 multi-atendente isolation
        const { idUser, idUserMain } = data;

        // 🚫 BLOQUEIA SOMENTE SE:
        // - não é minha
        // - não é contato novo da empresa
        const idUserIA = 1;

        const isMine = idUser === user.id;
        const isQueue = idUser === idUserMain;
        const isRobot = idUser === idUserIA;

        if (!isMine && !isQueue && !isRobot) {
          console.log('[SignalR][ReceiveMessageStatus] Ignorado - outro atendente');
          return;
        }

        handleStatusUpdate(data.idMessage, data.status);
      } catch (e) { console.error(e); }
    });

    const unsubSession = subscribeEvent<{ idUser: number, session: ISession }>("SessionUpdated", (data) => {
      try {
        // console.log("[MessagesContext] unsubSession", data);

        // 🚨 evento ainda chega antes do auth montar
        if (!user?.id) return;

        // 🧠 multi-atendente isolation
        const { idUser } = data;

        // 🚫 BLOQUEIA SOMENTE SE:
        // - não é minha
        // - não é contato novo da empresa
        const idUserIA = 1;

        const isMine = idUser === user.id;
        // const isQueue = idUser === idUserMain;
        const isRobot = idUser === idUserIA;

        if (!isMine && !isRobot) {
          console.log('[SignalR][SessionUpdated] Ignorado - outro atendente', data.session);
          // return;
        }

        handleSessionUpdated(data.session);
      } catch (e) { console.error(e); }
    });

    const unsubAssigned = subscribeEvent<{ notAssigneds: IContact[] }>("AssignedUpdated", (data) => {
      try {
        // console.log("[MessagesContext] unsubAssigned", data);
        handleAssignedUpdated(data.notAssigneds);
      } catch (e) { console.error(e); }
    });

    const unsubBookmark = subscribeEvent<{ idUser: number, bookmark: IContact[] }>("UpdateFavorites", (data) => {
      try {
        // console.log("[MessagesContext] Antes - unsubBookmark", data.idUser);

        // 🚨 evento ainda chega antes do auth montar
        if (!user?.id) return;

        // 🧠 multi-atendente isolation
        if (data.idUser !== user.id) return;

        // console.log("[MessagesContext] Depois - unsubBookmark", data.bookmark);
        setFavourites(data.bookmark); // nova referência        
      } catch (e) {
        console.error(e);
      }
    }
    );

    const unsubContactMarked = subscribeEvent<{ idUser: number, bookmark: IContact[] }>("UpdateMarked", (data) => {
      try {
        // console.log("[MessagesContext] Antes - unsubContactMarked", data.idUser);

        // 🚨 evento ainda chega antes do auth montar
        if (!user?.id) return;

        // 🧠 multi-atendente isolation
        if (data.idUser !== user.id) return;

        // console.log("[MessagesContext] Depois - unsubContactMarked", data.bookmark);

        setContactMarked(data.bookmark); // nova referência        
      } catch (e) {
        console.error(e);
      }
    }
    );

    const unsubContactBlocked = subscribeEvent<{ idUser: number, bookmark: IContact[] }>("UpdateBlocked", (data) => {
      try {
        console.log("[MessagesContext] unsubContactBlocked", data.bookmark);

        // 🚨 evento ainda chega antes do auth montar
        if (!user?.id) return;

        // 🧠 multi-atendente isolation
        //Nesse caso tem de aparece para todos atendentes
        // if (data.idUser !== user.id) return;

        setContactBlocked(data.bookmark); // nova referência        
      } catch (e) {
        console.error(e);
      }
    }
    );

    return () => {
      console.log("[SignalR] Unsubscribing events...");
      unsubStatus();
      unsubAttachments();
      unsubNew();
      unsubUpdate();
      unsubSession();
      unsubAssigned();
      unsubBookmark();
      unsubContactMarked();
      unsubContactBlocked();
    };
  }, [user?.id, connection?.state,  // 👈 ESSENCIAL
    // subscribeEvent, 
    handleIncomingMessage,
    upsertAttachments, handleStatusUpdate, handleSessionUpdated, handleAssignedUpdated]);


  const value = useMemo(() => ({
    lastSignalRMessageRef,
    lastMessage,
    updatedMessage,
    messages,
    sessions,
    contactNoAssigned,
    favourites,
    contactMarked,
    contactBlocked,
    loading,
    error,
    handleStatusUpdate,
    lastStatusUpdate,

    loadContactsBlocked,
    loadContactsMarked,
    loadFavourites,
    loadContactAssigned,
    loadSessionsActive,
    loadMessagesTypes,
    loadMessages,

    loadSessionContactOpen,
    loadAllUserConversations,
    loadMessagesNoUserAssigned,

    sendTransferSessionMessage,
    sendAudioTemplateMessage,
    sendAudioMessage,
    sendTemplateMessage,
    sendReactionMessage,
    sendCloseSessionMessage,
    sendMessage,
    updateMessage,
    deleteMessage,
    // novas funções
    upsertAttachments,
    getAttachmentsForMessage,
    markMessagesAsRead
  }), [
    lastSignalRMessageRef,
    lastMessage,
    updatedMessage,
    messages,
    sessions,
    contactNoAssigned,
    favourites,
    contactMarked,
    contactBlocked,
    loading,
    error,
    handleStatusUpdate,
    lastStatusUpdate,

    loadContactsBlocked,
    loadContactsMarked,
    loadFavourites,
    loadContactAssigned,
    loadSessionsActive,
    loadMessagesTypes,
    loadMessages,

    loadSessionContactOpen,
    loadAllUserConversations,
    loadMessagesNoUserAssigned,

    sendTransferSessionMessage,
    sendAudioTemplateMessage,
    sendAudioMessage,
    sendTemplateMessage,
    sendReactionMessage,
    sendCloseSessionMessage,
    sendMessage,
    updateMessage,
    deleteMessage,
    upsertAttachments,
    getAttachmentsForMessage,
    markMessagesAsRead,
  ]);

  return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>;
};

export const useMessages = () => {
  const ctx = useContext(MessagesContext);
  if (!ctx) throw new Error("useMessages must be used within MessagesProvider");
  return ctx;
};
